/**
 * **What the app checks a response against, at the boundary** — ADR 0025 decision 3's second
 * half, given a shape (issue #391).
 *
 * ## Why runtime schemas, and why only the app reads them
 *
 * A browser reloads; a phone carries the build it shipped with for as long as somebody
 * declines to update, so an old app and a new API will meet, and the app is the half that
 * cannot be fixed that afternoon. Decision 11 makes the server's side a standing rule — within
 * a major version a response may **gain** fields and may not **remove or retype** them — and
 * these are the app holding up its end: every `/app` response it reads is parsed against one
 * of these before a screen sees it, so a field the server stopped sending is an error with a
 * name rather than `undefined` rendered three screens later into a family's memory. The panel
 * does not read them and does not need to: it ships with its server.
 *
 * ## The rule this package has, and how a schema is allowed to break it
 *
 * `index.ts` says *"nothing in it may declare a response shape of its own"*, and a schema is
 * a response shape written down. It is admitted on one condition, which is the condition the
 * rule was protecting: **it may not be a second, unchecked statement of the contract.**
 * `services/api/tests/app/schemas-match-the-contract.test.ts` holds every schema here to the
 * server's own derived payload type — `SuccessPayload` off the route table, the thing decision
 * 3 made exist — by mutual assignability, in both directions, per route. Rename a field in
 * `reads.ts` and the *service's* build fails until the schema follows; add a field to a schema
 * the server does not send and the same build fails. So there is still exactly one source, the
 * route table, and this is derived from it in the only way a runtime artefact can be — by
 * being refused whenever it disagrees.
 *
 * The same test asserts completeness: every route the server serves has a schema here, and
 * every schema names a route the server serves.
 *
 * ## Unknown fields pass
 *
 * `z.object` strips keys it does not know rather than refusing them, which is the
 * compatibility rule from the app's side: an additive change on the server reaches an old app
 * as a response it can still read. A validator that rejected unknown keys would make every
 * addition a breaking one — the exact failure this exists to prevent.
 *
 * ## What is deliberately loose
 *
 * Enumerated strings that the server projects as plain `string` — an item's `status`, a
 * fact's `kind`, a horizon — are `z.string()` here too, because the server's type is
 * `string` and the screens already treat a value they have no words for as one to show rather
 * than swallow (`HORIZON_HEADING`'s rule). Where the server's type is a literal union the
 * schema is the same union, since a fourth value there is a retype under decision 11.
 */

import { z } from "zod";

// ── shared pieces ────────────────────────────────────────────────────────────

const nullableString = z.string().nullable();

/** One line of a write's `proposedChanges` / `appliedChanges` — `@pilotfish/db`'s `Change`. */
const change = z.object({
  entity: z.enum(["ITEM", "CONTEXT", "INVITE", "FEEDBACK", "FAMILY", "MEMBER"]),
  op: z.enum(["create", "update", "resolve", "send", "delete", "revoke"]),
  ref: z.string(),
  summary: z.string(),
  fields: z.record(z.string(), z.unknown()).optional(),
});

/** The tools' shared write envelope (ADR 0006 decision 5). */
const writeEnvelope = z.object({
  familyId: z.string(),
  actionId: nullableString,
  proposalId: nullableString,
  status: z.enum(["applied", "proposed"]),
  needsConfirmation: z.boolean(),
  proposedChanges: z.array(change),
  appliedChanges: z.array(change),
  message: z.string(),
});

const contextType = z.enum(["GOAL", "CONSTRAINT", "PREFERENCE", "DECISION", "FACT"]);
const importance = z.enum(["low", "medium", "high"]);
const memberRole = z.enum(["owner", "adult"]);

/** `@pilotfish/db`'s `ContextSubject` — three cases, because #92 says three. */
const contextSubject = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("family") }),
  z.object({ kind: z.literal("member"), memberId: z.string() }),
  z.object({ kind: z.literal("unstated") }),
]);

/** The envelope every paged read answers with (`reads.ts`). */
function paged<T extends z.ZodType>(record: T) {
  return z.object({
    records: z.array(record),
    nextCursor: nullableString,
    maxPageSize: z.number(),
  });
}

// ── /app/me ──────────────────────────────────────────────────────────────────

const membership = z.object({
  familyId: z.string(),
  name: nullableString,
  status: z.enum(["active", "deleting"]),
  role: memberRole.nullable(),
  memberCount: z.number().nullable(),
});

const cutOffConfirmation = z.object({
  message: z.string(),
  consequence: z.string(),
  caution: z.string(),
});

const connectedApp = z.discriminatedUnion("state", [
  z.object({
    connectedAppId: z.string(),
    claimedName: z.string(),
    claimedDescription: nullableString,
    can: z.array(z.string()),
    state: z.literal("connected"),
    residualMinutes: z.number(),
    confirm: cutOffConfirmation,
  }),
  z.object({
    connectedAppId: z.string(),
    claimedName: z.string(),
    state: z.literal("winding_down"),
    endsAt: z.string(),
  }),
]);

export const ME_SCHEMAS = {
  "GET /families": z.object({
    families: z.array(membership),
    nextStep: nullableString,
  }),
  "POST /families": z.object({
    familyId: z.string(),
    // `string | null` on the server, and the weld is what said so: the first draft here had
    // `z.string()`, which is exactly the second-statement drift the test exists to refuse.
    familyName: nullableString,
    role: memberRole,
    alreadyCreated: z.boolean(),
    accountEmail: z.string(),
  }),
  "GET /apps": z.object({
    apps: z.array(connectedApp),
    summary: z.object({
      connectedCount: z.number(),
      windingDownCount: z.number(),
      windingDownUntil: nullableString,
      nothingConnected: z.string(),
      confirmAll: cutOffConfirmation.nullable(),
    }),
    windingDownNote: z.string(),
  }),
  "POST /apps/revoke": z.object({
    connectedAppId: z.string(),
    claimedName: z.string(),
    endsAt: z.string(),
    residualMinutes: z.number(),
  }),
  "POST /apps/revoke-all": z.object({
    requested: z.number(),
    cutOff: z.number(),
    failed: z.number(),
    endsAt: nullableString,
    residualMinutes: z.number(),
  }),
  "POST /app-report": z.object({
    received: z.literal(true),
  }),
  /**
   * The second factor was sent (#307). `sentTo` is the caller's own verified address, so the
   * screen can say *which* inbox to look in; `expiresInMinutes` is the server's number rather
   * than one a client invented, so the two cannot disagree about how long a code lasts.
   */
  "POST /step-up": z.object({
    methodId: z.string(),
    sentTo: z.string(),
    expiresInMinutes: z.number(),
  }),
} as const;

// ── /app/families/:familyId reads ────────────────────────────────────────────

const disclosedItem = z.object({
  itemId: z.string(),
  title: z.string(),
  rawText: z.string(),
  itemType: z.string(),
  tags: z.array(z.string()),
  status: z.string(),
  timeHorizon: z.string(),
  priority: z.string(),
  dueDate: nullableString,
  raisedById: z.string(),
  raisedBy: nullableString,
  ownerId: nullableString,
  owner: nullableString,
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: nullableString,
});

const disclosedContext = z.object({
  ref: z.string(),
  kind: z.string(),
  title: z.string(),
  content: z.string(),
  rawText: z.string(),
  subject: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("family") }),
    z.object({ kind: z.literal("member"), name: nullableString }),
    z.object({ kind: z.literal("unstated") }),
  ]),
  subjectMemberId: nullableString,
  importance: z.string(),
  confirmed: z.boolean(),
  rationale: nullableString,
  status: z.string(),
  archivedAt: nullableString,
  expiresAt: nullableString,
  createdAt: z.string(),
  updatedAt: z.string(),
});

const disclosedChange = z.object({
  entity: z.string(),
  op: z.string(),
  summary: z.string(),
});

const disclosedAction = z.object({
  actionId: z.string(),
  by: nullableString,
  tool: z.string(),
  summary: z.string(),
  proposed: z.array(disclosedChange),
  applied: z.array(disclosedChange),
  status: z.string(),
  via: z.string(),
  askedVia: nullableString,
  keptForGood: z.boolean(),
  redacted: z.boolean(),
  redactedAt: nullableString,
  at: z.string(),
});

const rosterMember = z.object({
  userId: z.string(),
  // `string | undefined` on the server — a `Map.get` over the roster — and required as a key,
  // which is what the union rather than `.optional()` says: the key is always there.
  name: z.union([z.string(), z.undefined()]),
  email: z.string(),
  role: memberRole,
  joinedAt: z.string(),
  isYou: z.boolean(),
});

const pendingInvitation = z.object({
  email: z.string(),
  role: memberRole,
  invitedBy: nullableString,
  invitedAt: z.string(),
  expiresAt: z.string(),
  expired: z.boolean(),
});

const archiveEntry = z.object({
  id: z.string(),
  fields: z.record(z.string(), z.unknown()),
});

const archiveSection = z.object({
  section: z.enum([
    "family",
    "members",
    "invitations",
    "backlog",
    "memory",
    "pending",
    "notifications",
    "feedback",
    "history",
  ]),
  label: z.string(),
  entries: z.array(archiveEntry),
});

export const FAMILY_READ_SCHEMAS = {
  "GET /memory": paged(disclosedContext),
  "GET /backlog": paged(disclosedItem),
  "GET /activity": paged(disclosedAction).extend({ everythingKeptSince: z.string() }),
  "GET /roster": z.object({
    members: z.array(rosterMember),
    invitations: z.array(pendingInvitation),
    canAdminister: z.boolean(),
  }),
  "GET /archive": z.object({
    snapshotAt: z.string(),
    sections: z.array(archiveSection),
    totalRecords: z.number(),
    unreadable: z.number(),
    doesNotInclude: z.array(z.string()),
  }),
} as const;

// ── /app/families/:familyId writes — each one its tool's own output ──────────

const duplicateMatch = z.object({
  candidateIndex: z.number(),
  itemId: z.string(),
  existingTitle: z.string(),
  score: z.number(),
});

const collisionSummary = z.object({
  contextType,
  title: z.string(),
  content: z.string(),
  subject: contextSubject,
  importance,
  confirmed: z.boolean(),
  rationale: z.string().optional(),
});

const rosterWrite = writeEnvelope.extend({ subject: nullableString });

export const FAMILY_WRITE_SCHEMAS = {
  "POST /capture": writeEnvelope.extend({ duplicates: z.array(duplicateMatch) }),
  "POST /resolve": writeEnvelope,
  "POST /update": writeEnvelope,
  "POST /remember": writeEnvelope.extend({ collision: collisionSummary.nullable() }),
  "POST /forget-this": writeEnvelope.extend({
    deleting: z
      .object({
        contextType,
        title: z.string(),
        auditEntries: z.number(),
        quotedOnOtherFacts: z.number(),
      })
      .nullable(),
    doesNotReach: z.array(z.string()),
  }),
  "POST /correct-this": writeEnvelope.extend({
    correcting: z
      .object({ contextType, from: z.string(), to: z.string(), movesKey: z.boolean() })
      .nullable(),
  }),
  "POST /delete-item": writeEnvelope.extend({
    deleting: z
      .object({
        itemId: z.string(),
        title: z.string(),
        status: z.enum(["open", "done", "dropped"]),
        auditEntries: z.number(),
        quotedOnFacts: z.number(),
      })
      .nullable(),
    doesNotReach: z.array(z.string()),
  }),
  "POST /invite-member": writeEnvelope.extend({ inviteLink: nullableString }),
  "POST /revoke-invitation": writeEnvelope.extend({
    withdrawn: z.number().nullable(),
    stillOutstanding: z.number().nullable(),
  }),
  "POST /remove-member": rosterWrite,
  "POST /leave-family": rosterWrite,
  "POST /transfer-ownership": rosterWrite,
  "POST /disband-family": writeEnvelope.extend({
    deleting: z
      .object({
        items: z.number(),
        context: z.number(),
        invitations: z.number(),
        members: z.number(),
      })
      .nullable(),
    doesNotReach: z.array(z.string()),
    erased: z.boolean().nullable(),
  }),
} as const;

export const FAMILY_SCHEMAS = { ...FAMILY_READ_SCHEMAS, ...FAMILY_WRITE_SCHEMAS } as const;

/** Every route the app can read, keyed as the server's own route tables are. */
export const RESPONSE_SCHEMAS = { me: ME_SCHEMAS, family: FAMILY_SCHEMAS } as const;

export type MeRouteKey = keyof typeof ME_SCHEMAS;
export type FamilyRouteKey = keyof typeof FAMILY_SCHEMAS;

/** What a schema parses to — the type the app renders from, derived here and welded there. */
export type Parsed<S extends z.ZodType> = z.output<S>;
